import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
  ColumnDef as TanStackColumnDef,
  useReactTable,
} from "@tanstack/react-table";
import React, { Fragment } from "react";
import { Button, Row, Table } from "react-bootstrap";
import UsersTypeFilterPopover from "./UserFilterPopOver";
import DateFilterPopover from "Common/CusomizeTableComponents/DateFilterPopover";
import DateRangeFilterPopover from "Common/CusomizeTableComponents/DateRangeFilterPopover";
import Pagination from "Components/Pagination/Pagination";

type FilterType = "dropdown" | "text" | "date" | "boolean" | "singleDate";

export type CustomColumnDef<TData> = TanStackColumnDef<TData> & {
  filterType?: FilterType;
  filterOptions?: string[];
  filterFetchUrl?: string;
};

interface TableContainerProps<TData = any> {
  columns: CustomColumnDef<TData>[];
  data: TData[];
  totalRecords: number;
  pageIndex: number;
  pageSize: number;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  columnFilters?: ColumnFiltersState;
  setColumnFilters?: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  colValue: any;
  isBordered?: boolean;
  isPagination?: boolean;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  manualSorting: boolean;
  isGlobalFilter?: boolean;
  SearchPlaceholder?: string;
}

const OrganisationsTableContainer = ({
  columns,
  data,
  totalRecords,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  columnFilters,
  setColumnFilters,
  sorting,
  onSortingChange,
  manualSorting = true,
  isBordered = false,
  isPagination = true,
  colValue,
}: TableContainerProps) => {
  const handleColumnFilterChange: OnChangeFn<ColumnFiltersState> = (
    updaterOrValue
  ) => {
    if (!setColumnFilters) return;
    setColumnFilters((prev: any) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(prev)
        : updaterOrValue
    );
  };

  const table = useReactTable({
    columns,
    data,
    manualPagination: true,
    manualFiltering: true,
    manualSorting,
    pageCount: Math.ceil(totalRecords / pageSize),
    state: {
      sorting,
      pagination: {
        pageIndex,
        pageSize,
      },
      columnFilters,
    },
    onSortingChange,
    onColumnFiltersChange: handleColumnFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: !manualSorting ? getSortedRowModel() : undefined,
  });

  const { getHeaderGroups, getRowModel, getCanPreviousPage, getCanNextPage } =
    table;

  const totalPages = Math.ceil(totalRecords / pageSize);
  const maxPageButtons = 5;

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= maxPageButtons) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      if (pageIndex <= 2) pages.push(0, 1, 2, -1, totalPages - 1);
      else if (pageIndex >= totalPages - 3)
        pages.push(0, -1, totalPages - 3, totalPages - 2, totalPages - 1);
      else
        pages.push(
          0,
          -1,
          pageIndex - 1,
          pageIndex,
          pageIndex + 1,
          -1,
          totalPages - 1
        );
    }
    return pages;
  };

  return (
    <Fragment>
      <style>
        {`
        .sticky-column {
          position: sticky;
          right: 0;
          z-index: 10;
          background-color: #fff !important;
          border-left: 1px solid #dee2e6;
        }

        thead .sticky-column {
          background-color: #EEF0F7 !important;
        }

        .sticky-action {
          box-shadow: -4px 0 6px rgba(0, 0, 0, 0.1);
        }

        .table td,
        .table th {
          background-clip: padding-box;
        }
      `}
      </style>

      <div className="table-responsive table-card">
        <Table
          className={`table table-centered align-middle table-custom-effect table-nowrap mb-0 ${
            isBordered ? "table-bordered" : ""
          }`}
          bordered={isBordered}
        >
          <thead className="table-light">
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const isLastColumn = index === headerGroup.headers.length - 1;
                  const isSorted = header.column.getIsSorted();
                  const canSort = header.column.getCanSort();
                  const isRemark = header.column.columnDef.header === "Remark";

                  const sortIcon =
                    isSorted === "asc" ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : isSorted === "desc" ? (
                      <ArrowDownwardIcon fontSize="small" />
                    ) : (
                      <UnfoldMoreIcon fontSize="small" />
                    );

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`align-middle ${
                        isLastColumn ? "sticky-column sticky-action" : ""
                      } ${isRemark ? "remark-cell" : ""}`}
                      style={
                        isRemark
                          ? {
                              minWidth: "180px",
                              maxWidth: "300px",
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                            }
                          : undefined
                      }
                    >
                      {!header.isPlaceholder && (
                        <div
                          className="d-flex justify-content-between align-items-center"
                          style={{ gap: 4 }}
                        >
                          <div
                            className="d-flex align-items-center"
                            style={{
                              gap: 4,
                              cursor: canSort ? "pointer" : "default",
                            }}
                            onClick={
                              canSort
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                          >
                            <span>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </span>
                            {canSort && sortIcon}
                          </div>

                          {header.column.getCanFilter()
                            ? (() => {
                                const filterType = (
                                  header.column
                                    .columnDef as CustomColumnDef<any>
                                ).filterType;
                                if (filterType === "date") {
                                  return (
                                    <DateRangeFilterPopover
                                      column={header.column}
                                      setColValue={colValue}
                                      iconColor={
                                        header.column.getFilterValue()
                                          ? "#3258d3"
                                          : "inherit"
                                      }
                                    />
                                  );
                                }

                                if (filterType === "singleDate") {
                                  return (
                                    <DateFilterPopover
                                      column={header.column}
                                      setColValue={colValue}
                                      iconColor={
                                        header.column.getFilterValue()
                                          ? "#3258d3"
                                          : "inherit"
                                      }
                                    />
                                  );
                                }

                                return (
                                  <UsersTypeFilterPopover
                                    column={header.column}
                                    columnDef={header.column.columnDef}
                                    columnFilters={columnFilters}
                                    setColumnFilters={setColumnFilters}
                                    setColValue={colValue}
                                    iconColor={
                                      header.column.getFilterValue()
                                        ? "#3258d3"
                                        : "inherit"
                                    }
                                  />
                                );
                              })()
                            : null}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  No Data Found
                </td>
              </tr>
            ) : (
              getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell, index) => {
                    const isLastColumn =
                      index === row.getVisibleCells().length - 1;
                    const isRemark = cell.column.columnDef.header === "Remark";

                    return (
                      <td
                        key={cell.id}
                        className={`${
                          isLastColumn ? "sticky-column sticky-action" : ""
                        } ${isRemark ? "remark-cell" : ""}`}
                        style={
                          isRemark
                            ? {
                                maxWidth: "300px",
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                              }
                            : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {isPagination && (
        <Row className="align-items-center mt-4 pt-3 gy-2">
          <Pagination
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Row>
      )}
    </Fragment>
  );
};

export default OrganisationsTableContainer;
